import {
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
} from '@material-ui/core';
import {useTheme} from '@material-ui/core/styles';
import {makeStyles} from '@material-ui/core/styles';
import {Close} from '@material-ui/icons';
import DirectionsBoatIcon from '@material-ui/icons/DirectionsBoat';
import {PBold} from 'components/TypographyShortcuts';
import fields, {shortFields} from './fields';
import {useContext, useEffect} from 'react';
import {FormProvider} from 'react-hook-form';
import {useNonPersistentFormMethods} from '../hooks';
import {bootstrapOldForm} from '../AutoSection';
import {PrintField} from './FieldDetail';
import {ErrorPage} from 'components/LoadingPage';
import {usePostError, errorDataHelper} from 'hooks';
import {cleanVessel} from 'hooks/useGetEndpoint';
import {withMiniFormProvider} from './MiniFormContext';
import {MiniFormContext} from './MiniFormContext';
import VesselModalFormFields from './VesselModalFormFields';
import {sanitizeFormInput} from './VesselEndSection';
import usePostVesselData from 'hooks/usePostVesselData';

const useStyles = makeStyles(theme => ({
    root: {
        margin: 0,
        padding: theme.spacing(2),
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
    },
}));

const safeMergeInitialValues = (initialValues, vessel) => {
    // need something only return vessel fields, and when vessel data is null use correct
    // type from initial values
    const outData = {};
    // initialValues already knows all fields we want to keep
    Object.entries(initialValues).forEach(([key, value]) => {
        if (
            vessel[key] === null ||
            vessel[key] === undefined ||
            vessel[key] === ''
        ) {
            outData[key] = value;
        } else {
            outData[key] = vessel[key];
        }
    });
    return outData;
};

const PlainVesselModal = ({vessel, open, setOpen}) => {
    const classes = useStyles();
    const close = () => {
        setOpen(false);
    };
    const theme = useTheme();
    return (
        <Dialog open={open} onClose={close} maxWidth='xs' fullWidth>
            <DialogTitle className={classes.root}>
                <span style={{flexGrow: 1}}>{vessel?.name}</span>
                <IconButton
                    onClick={close}
                    className={classes.closeButton}
                    data-cy='close-vessel-detail'
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <div style={{display: 'flex'}}>
                            <DirectionsBoatIcon
                                style={{
                                    fontSize: 80,
                                    color: '#424242',
                                    margin: 'auto',
                                    backgroundColor: theme.palette.gray.light,
                                }}
                            />
                        </div>
                    </Grid>
                    <Grid item xs={12}>
                        {Object.keys(fields).map(field => (
                            <PrintField
                                name={field}
                                value={vessel?.[field]}
                                key={field}
                            />
                        ))}
                    </Grid>
                    <Grid item xs={12} />
                </Grid>
            </DialogContent>
        </Dialog>
    );
};

const VesselModal = ({
    vessel,
    updateVessel,
    selected,
    handleSelectVessel = () => {},
    handleDeselectVessel = () => {},
    handleEditVessel = () => {},
    open,
    setOpen,
}) => {
    const classes = useStyles();
    const {setFormEditable} = useContext(MiniFormContext);
    const {formPost, submissionInProgress, result, error, reset} =
        usePostVesselData();
    const {post: postError} = usePostError();
    const theme = useTheme();

    const close = () => {
        reset();
        setOpen(false);
    };

    const chooseVessel = data => {
        // post the new data if we need to
        const didPost = formPost(data, vessel);
        if (!didPost) {
            // form post will return false if it doesn't need to post
            // in this case just update the selected vessel
            handleSelectVessel(vessel);
        }
    };

    useEffect(() => {
        if (result) {
            const newVessel = sanitizeFormInput(cleanVessel(result));
            handleSelectVessel(newVessel);
            updateVessel(newVessel);
            reset();
        }
        //eslint-disable-next-line
    }, [result]);

    useEffect(() => {
        if (error) {
            // log a ticket if vessel post fails
            postError(
                errorDataHelper({
                    feedback_text: `[AUTO GENERATED][VESSEL FORM SUBMIT ERROR] Result from server: ${JSON.stringify(
                        error,
                        null,
                        2
                    )}`,
                })
            );
        }
        //eslint-disable-next-line
    }, [error]);

    const unchooseVessel = () => {
        handleDeselectVessel(null);
    };

    const {initialValues, schema} = bootstrapOldForm('', shortFields);

    const methods = useNonPersistentFormMethods({
        initialValues: safeMergeInitialValues(initialValues, vessel),
        schema: schema,
    });

    useEffect(() => {
        setFormEditable(selected !== vessel);
        //eslint-disable-next-line
    }, [selected, vessel]);

    return (
        <Dialog open={open} onClose={close} maxWidth='xs' fullWidth>
            <DialogTitle className={classes.root}>
                <span style={{flexGrow: 1}}>{vessel?.name}</span>
                <IconButton onClick={close} className={classes.closeButton}>
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <FormProvider {...methods}>
                    <form onSubmit={methods.handleSubmit(chooseVessel)}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <div style={{display: 'flex'}}>
                                    <DirectionsBoatIcon
                                        style={{
                                            fontSize: 80,
                                            color: '#424242',
                                            margin: 'auto',
                                            backgroundColor:
                                                theme.palette.gray.light,
                                        }}
                                    />
                                </div>
                            </Grid>
                            <VesselModalFormFields
                                vessel={vessel}
                                selected={selected}
                                unchooseVessel={unchooseVessel}
                            />
                            {selected === vessel ? (
                                [
                                    <Grid item xs={12} key='selected'>
                                        <PBold align='center'>
                                            This vessel is currently selected
                                        </PBold>
                                    </Grid>,
                                    <Grid item xs={12} key='deselect-button'>
                                        <Button
                                            fullWidth
                                            color='secondary'
                                            variant='outlined'
                                            onClick={unchooseVessel}
                                        >
                                            Don't Use this Vessel
                                        </Button>
                                    </Grid>,
                                    <Grid item xs={12} key='edit-advanced'>
                                        <Button
                                            fullWidth
                                            variant='outlined'
                                            color='primary'
                                            onClick={() =>
                                                handleEditVessel(vessel)
                                            }
                                        >
                                            Edit Vessel
                                        </Button>
                                    </Grid>,
                                ]
                            ) : submissionInProgress ? (
                                <Grid item xs={12}>
                                    <Button
                                        fullWidth
                                        disabled
                                        variant='contained'
                                    >
                                        Saving Changes... <CircularProgress />
                                    </Button>
                                </Grid>
                            ) : error ? (
                                <Grid item xs={12}>
                                    <ErrorPage error={error} />
                                </Grid>
                            ) : (
                                [
                                    <Grid item xs={12} key='save-btn'>
                                        <Button
                                            fullWidth
                                            color='primary'
                                            variant='contained'
                                            type='submit'
                                            data-cy='use-this-vessel'
                                        >
                                            Use this Vessel
                                        </Button>
                                    </Grid>,
                                    <Grid item xs={12} key='edit-advanced-btn'>
                                        <Button
                                            fullWidth
                                            style={{
                                                background:
                                                    theme.palette.primary
                                                        .light2,
                                            }}
                                            variant='contained'
                                            onClick={() =>
                                                handleEditVessel(vessel)
                                            }
                                            data-cy='edit-advanced-fields'
                                        >
                                            Edit Advanced Fields
                                        </Button>
                                    </Grid>,
                                ]
                            )}
                            <Grid item />
                        </Grid>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    );
};

export default withMiniFormProvider(VesselModal);
export {PlainVesselModal};
